/**
 * Everything a mission puts on screen, built once from the `MissionDef` and fed the world each
 * frame: terrain and props, actors, effects, the Osprey, the drop, weather, the pixel pipeline.
 * It reads the sim and never writes to it (06 §R3).
 */

import { BufferAttribute, Mesh, PlaneGeometry, Scene, type WebGLRenderer } from 'three';
import { PixelCamera } from '../render/camera/PixelCamera';
import { createGradientMap, createToonMaterial, OUTLINE, sharedUniforms } from '../render/materials/toon';
import { getGrade, Palette } from '../render/palette';
import { RenderPipeline } from '../render/pipeline/RenderPipeline';
import { Effects } from '../render/vfx/effects';
import { Weather } from '../render/vfx/weather';
import { castOf } from '../render/world/actorLooks';
import { Actors } from '../render/world/Actors';
import { InsertionView } from '../render/world/InsertionView';
import { OspreyView } from '../render/world/OspreyView';
import { SearchlightView } from '../render/world/SearchlightView';
import { SmokeView } from '../render/world/SmokeView';
import { Wake } from '../render/world/Wake';
import { lightning } from '../sim/weather';
import { StaticWorld } from '../render/world/StaticWorld';
import type { MissionDef, SimEvent, World } from '../sim/types';
import { MissionCamera } from './missionCamera';

/** Comfort options that change what is drawn (08 §4). */
export interface ViewOptions {
  shake: boolean;
  flashes: boolean;
  crt: boolean;
}

export class MissionView {
  readonly scene = new Scene();
  readonly palette: Palette;
  readonly camera: PixelCamera;
  readonly pipeline: RenderPipeline;
  readonly world3d: StaticWorld;
  readonly effects: Effects;
  private readonly missionCamera: MissionCamera;
  private readonly actors: Actors;
  private readonly osprey: OspreyView;
  private readonly insertion: InsertionView;
  private readonly smoke: SmokeView;
  private readonly wake: Wake;
  private readonly searchlights: SearchlightView;
  private readonly weather: Weather;
  private readonly lut: ReturnType<Palette['buildLut']>;
  /** Pixels per metre of the ground view: the zoom the weather's box is sized for. */
  private readonly groundPpm: number;

  constructor(
    private readonly renderer: WebGLRenderer, private readonly def: MissionDef, world: World,
    ppm: number, pitch: number,
    options: ViewOptions = { shake: true, flashes: true, crt: false },
  ) {
    const palette = new Palette(def.biome);
    this.palette = palette;
    this.groundPpm = ppm;
    const grade = getGrade(def.timeOfDay);
    const gradient = createGradientMap();
    const { scene } = this;
    this.camera = new PixelCamera(ppm, pitch);
    this.missionCamera = new MissionCamera(this.camera, ppm);

    this.world3d = new StaticWorld(scene, def, palette, grade, gradient);
    const groundY = this.world3d.groundY;
    // The cast: who the mission is about, so the general, the courier and the scientist can be told from the garrison.
    this.actors = new Actors(scene, palette, gradient, groundY, castOf(def));
    this.effects = new Effects({
      scene, palette, gradientMap: gradient, groundY,
      propPosition: (id) => this.world3d.propPosition(id),
      emissiveBoost: def.timeOfDay === 'night' ? 1.2 : 0.45,
      // ROUND5 §4: a blizzard or a sandstorm scours footprints away, on screen as in the sim.
      sky: def,
    });
    this.osprey = new OspreyView(scene, palette, createToonMaterial({ gradientMap: gradient, outline: OUTLINE.prop }), def, groundY);
    this.insertion = new InsertionView(scene, palette, gradient, groundY);
    this.smoke = new SmokeView(scene, palette, gradient, groundY);
    // Wading was four rules and no picture: the waterline round him, and rings off it (Wake.ts).
    this.wake = new Wake(scene, palette, gradient, def, groundY);
    // Built at every time of day: besides the beams after dark, it is what turns the lamp heads.
    this.searchlights = new SearchlightView(scene, palette, gradient, groundY, {
      timeOfDay: def.timeOfDay, head: (id) => this.world3d.turningPart(id),
    });
    // A resumed mission: what was blown up before the save stays blown up.
    for (const p of world.props) if (p.destroyed) this.world3d.destroyProp(p.id, p.removed !== true);

    // From the aircraft the view is three times wider than the map: an apron of plain ground
    // in the biome's colour stands in for the country beyond it, just below the real terrain.
    const apron = new Mesh(new PlaneGeometry(900, 900), createToonMaterial({ gradientMap: gradient, outline: OUTLINE.none }));
    apron.geometry.rotateX(-Math.PI / 2);
    const ground = palette.color(def.biome === 'arctic' ? 'ground.3' : 'ground.2');
    const colours = new Float32Array(4 * 3);
    for (let i = 0; i < 4; i++) colours.set([ground.r, ground.g, ground.b], i * 3);
    apron.geometry.setAttribute('color', new BufferAttribute(colours, 3));
    apron.position.set(def.sizeX / 2, -1.6, def.sizeZ / 2);
    apron.receiveShadow = true;
    scene.add(apron);

    this.weather = new Weather(def.weather, palette.color(def.biome === 'desert' ? 'ground.3' : 'paper.0'), { groundY });
    if (this.weather.points) scene.add(this.weather.points);

    this.lut = palette.buildLut();
    this.pipeline = new RenderPipeline(renderer, this.lut, palette.color('ink.0'));
    this.pipeline.setGrade(grade, palette.haze);
    this.pipeline.setPlayerRim(def.timeOfDay === 'night' ? palette.hex('paper.0') : null);
    const dark = def.timeOfDay === 'night' || def.timeOfDay === 'dusk';
    this.pipeline.setOptions({
      nvg: world.ranger.gear.nvg && dark,
      nightPenalty: world.mods.nightPenalty,
      crt: options.crt,
      // Photosensitivity-safe means both: no full-strength flashes, and a cap on what is left.
      flashCap: !options.flashes,
    });
    this.effects.setOptions({ flashes: options.flashes, shake: options.shake });
  }

  /** A prop left the map whole (a jet that scrambled): take it away, leave no wreck. */
  removeProp(id: number): void {
    this.world3d.destroyProp(id, false);
  }

  /** A prop went up this tick: swap its model for the wreck at once, not a frame later. */
  destroyProp(id: number): void {
    this.world3d.destroyProp(id);
  }

  /** Draw one frame. `dt` is 0 while paused; `time` is the mission's own running clock. */
  present(world: World, events: readonly SimEvent[], time: number, dt: number, alpha: number): void {
    sharedUniforms.uTime.value = time;
    this.effects.handle(events as SimEvent[]);
    this.effects.update(world, dt, alpha, this.camera);
    this.missionCamera.update(world, alpha, dt, this.effects.shake.x, this.effects.shake.z);

    this.world3d.updateShadows(this.camera);
    // Breakers thrown, generators killed, the strike beacon stood up: read off the world, so a resumed mission is right too.
    this.world3d.syncPropStates(world, time);
    this.actors.handle(events);
    this.actors.update(world, time, alpha, this.camera);
    this.osprey.update(world, time, dt, this.camera);
    this.insertion.update(world, time, dt, alpha, this.camera);
    // A thunderstorm throws lightning (02 §5). Derived from the tick and the mission's seed, so
    // the sim, the sound and the sky all read the same stroke without anything being passed between
    // them - and a replay lights up on exactly the tick it did the first time.
    this.world3d.setFlash(lightning(this.def, this.def.seed, world.tick));
    this.wake.update(world, dt, alpha);
    this.smoke.update(world, time, this.camera);
    this.searchlights.update(world, this.camera);
    this.weather.update(time, this.camera.focus, this.groundPpm / this.camera.pixelsPerMetre);

    this.renderer.info.reset();
    this.pipeline.render(this.scene, this.camera);
  }

  dispose(): void {
    this.effects.dispose();
    this.actors.dispose();
    this.smoke.dispose();
    this.wake.dispose();
    this.searchlights.dispose();
    this.world3d.dispose();
    this.scene.traverse((object) => {
      const mesh = object as { geometry?: { dispose(): void }; material?: { dispose(): void } | Array<{ dispose(): void }> };
      mesh.geometry?.dispose();
      if (Array.isArray(mesh.material)) for (const m of mesh.material) m.dispose();
      else mesh.material?.dispose();
    });
    this.lut.dispose();
    this.pipeline.dispose();
  }
}
